import { useCallback } from 'react';
import { TableProps } from './types';
import { FixedHeaderTable } from './components/FixedHeaderTable';
import { TableHead, TableHeader, TableRow, TableCell, Skeleton } from '@leadsnavi/ui-kit';
import React from 'react';
import cn from 'classnames';

type TableRowV2Props<T> = Pick<TableProps<T>, 'onRowExtraRender' | 'columns'> & {
  row: T;
  index: number;
  onRowClick?: (row: T) => void;
};
const TableRowV2 = <T,>(props: TableRowV2Props<T>) => {
  const { row, index, columns, onRowClick, onRowExtraRender } = props;
  return (
    <React.Fragment key={index}>
      <TableRow
        key={index}
        className={cn(' group', onRowClick ? 'cursor-pointer' : '', {
          'border-b-0': !!onRowExtraRender,
        })}
        onClick={() => onRowClick?.(row)}
      >
        {columns.map(column => (
          <TableCell
            key={column.key}
            className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
            style={{ width: column.width }}
          >
            {column.render
              ? column.render(row[column.dataIndex as keyof T], row)
              : (row[column.dataIndex as keyof T] as React.ReactNode)}
          </TableCell>
        ))}
      </TableRow>
      {onRowExtraRender ? (
        <TableRow className={cn(onRowClick ? 'cursor-pointer' : '', {})}>
          <TableCell className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={columns.length}>
            {onRowExtraRender(row)}
          </TableCell>
        </TableRow>
      ) : null}
    </React.Fragment>
  );
};

export const Table = <T,>(props: TableProps<T>) => {
  const {
    isFixed = true,
    data,
    columns,
    onRowClick,
    loading = false,
    emptyText = 'No data available',
    className = '',
    height = 500,
    onRowExtraRender,
  } = props;
  const handleRowClick = useCallback(
    (row: T) => {
      if (onRowClick) {
        onRowClick(row);
      }
    },
    [onRowClick]
  );

  // 渲染表头
  const renderHeader = () => (
    <TableHeader>
      <TableRow>
        {columns.map(column => (
          <TableHead
            key={column.key}
            className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}
            style={{ width: column.width }}
          >
            {column.title}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  );

  // 渲染加载状态
  const renderLoading = () => (
    <TableRow>
      {columns.map(column => (
        <TableCell key={column.key} className="px-6 py-4">
          <Skeleton className="h-4 w-full" />
        </TableCell>
      ))}
    </TableRow>
  );

  // 渲染空状态
  const renderEmpty = () => (
    <TableRow>
      <TableCell colSpan={columns.length} className="px-6 py-12 text-center">
        {/* <EmptyPlaceholder text={emptyText} /> */}
        {emptyText}
      </TableCell>
    </TableRow>
  );

  // 渲染数据行
  const renderRows = () =>
    data.map((row, index) => (
      <TableRowV2
        columns={columns}
        key={index}
        row={row}
        index={index}
        onRowClick={handleRowClick}
        onRowExtraRender={onRowExtraRender}
      />
    ));

  return (
    <FixedHeaderTable header={renderHeader()} height={height} isFixed={isFixed} className={className}>
      {loading
        ? Array(3)
            .fill(0)
            .map(_ => renderLoading())
        : data.length === 0
          ? renderEmpty()
          : renderRows()}
    </FixedHeaderTable>
  );
};
